import {PayloadAction, createSlice} from '@reduxjs/toolkit'
import {
  VideoGalleryState,
  FetchVideoGalleryResponse,
  FetchAllVideoGalleryResponse,
  FetchVideoGalleryByIdResponse,
  DeleteVideoGalleryRequest,
} from './types'
import {
  fetchVideoGallery,
  fetchAllVideoGallery,
  fetchVideoGalleryById,
  createVideoGallery,
  updateVideoGallery,
  deleteVideoGallery,
} from './service'

const initialState: VideoGalleryState = {
  videoForEdit: null,
  videos: [],
  total: 0,
  totalPages: 0,
  listLoading: false,
  actionLoading: false,
  error: null,
}

const videoGallerySlice = createSlice({
  name: 'videoGallery',
  initialState,
  reducers: {
    resetVideoGallery: (state: VideoGalleryState) => {
      state.videos = []
      state.total = 0
      state.totalPages = 0
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchVideoGallery.pending, (state: VideoGalleryState) => {
      state.listLoading = true
      state.error = null
    })
    builder.addCase(fetchVideoGallery.rejected, (state: VideoGalleryState, action: PayloadAction<unknown>) => {
      const payload = action.payload as {error?: string}
      state.error = payload?.error ?? null
      state.listLoading = false
    })
    builder.addCase(
      fetchVideoGallery.fulfilled,
      (state: VideoGalleryState, action: PayloadAction<FetchVideoGalleryResponse>) => {
        state.listLoading = false
        state.error = null
        const {videos, total, totalPages} = action.payload
        state.videos = videos
        state.total = total
        state.totalPages = totalPages
      }
    )

    builder.addCase(fetchAllVideoGallery.pending, (state: VideoGalleryState) => {
      state.listLoading = true
      state.error = null
    })
    builder.addCase(fetchAllVideoGallery.rejected, (state: VideoGalleryState, action: PayloadAction<unknown>) => {
      const payload = action.payload as {error?: string}
      state.error = payload?.error ?? null
      state.listLoading = false
    })
    builder.addCase(
      fetchAllVideoGallery.fulfilled,
      (state: VideoGalleryState, action: PayloadAction<FetchAllVideoGalleryResponse>) => {
        state.listLoading = false
        state.error = null
        state.videos = action.payload.videos
      }
    )

    builder.addCase(fetchVideoGalleryById.pending, (state: VideoGalleryState) => {
      state.listLoading = true
      state.error = null
    })
    builder.addCase(fetchVideoGalleryById.rejected, (state: VideoGalleryState, action: PayloadAction<unknown>) => {
      const payload = action.payload as {error?: string}
      state.error = payload?.error ?? null
      state.listLoading = false
    })
    builder.addCase(
      fetchVideoGalleryById.fulfilled,
      (state: VideoGalleryState, action: PayloadAction<FetchVideoGalleryByIdResponse>) => {
        state.listLoading = false
        state.error = null
        state.videoForEdit = action.payload.video
      }
    )

    builder.addCase(createVideoGallery.pending, (state: VideoGalleryState) => {
      state.actionLoading = true
      state.error = null
    })
    builder.addCase(createVideoGallery.rejected, (state: VideoGalleryState, action: PayloadAction<unknown>) => {
      const payload = action.payload as {error?: string}
      state.error = payload?.error ?? null
      state.actionLoading = false
    })
    builder.addCase(
      createVideoGallery.fulfilled,
      (state: VideoGalleryState, action: PayloadAction<FetchVideoGalleryByIdResponse>) => {
        state.actionLoading = false
        state.error = null
        state.videoForEdit = action.payload.video
        state.videos = [action.payload.video, ...state.videos]
        state.total = state.total + 1
      }
    )

    builder.addCase(updateVideoGallery.pending, (state: VideoGalleryState) => {
      state.actionLoading = true
      state.error = null
    })
    builder.addCase(updateVideoGallery.rejected, (state: VideoGalleryState, action: PayloadAction<unknown>) => {
      const payload = action.payload as {error?: string}
      state.error = payload?.error ?? null
      state.actionLoading = false
    })
    builder.addCase(
      updateVideoGallery.fulfilled,
      (state: VideoGalleryState, action: PayloadAction<FetchVideoGalleryByIdResponse>) => {
        state.actionLoading = false
        state.error = null
        state.videoForEdit = action.payload.video
        const idx = state.videos.findIndex((v) => v._id === action.payload.video._id)
        if (idx >= 0) state.videos[idx] = action.payload.video
      }
    )

    builder.addCase(deleteVideoGallery.pending, (state: VideoGalleryState) => {
      state.actionLoading = true
      state.error = null
    })
    builder.addCase(deleteVideoGallery.rejected, (state: VideoGalleryState, action: PayloadAction<unknown>) => {
      const payload = action.payload as {error?: string}
      state.error = payload?.error ?? null
      state.actionLoading = false
    })
    builder.addCase(deleteVideoGallery.fulfilled, (state: VideoGalleryState, action) => {
      const deletedId =
        (action.payload as {video?: {_id?: string}})?.video?._id ?? (action.meta.arg as DeleteVideoGalleryRequest)?.id
      if (deletedId) {
        state.videos = state.videos.filter((v) => v._id !== deletedId)
        state.total = Math.max(0, state.total - 1)
      }
      state.videoForEdit = null
      state.actionLoading = false
      state.error = null
    })
  },
})

export default videoGallerySlice.reducer
export const videoGalleryActions = videoGallerySlice.actions
